<template>
  <Dialog v-model="currentVisible" :title="$t('Channel')">
    <Form ref="channelForm" @submit="onSubmit('channelForm')">
      <Row gutter="md">
        <Col col="12">
          <Toggle
            :value="formData.enabled"
            :label="$t('Enabled')"
            :disabled="loading"
            @input="onChangeEnabled"
          />
        </Col>
        <Col col="12">
          <Input
            name="name"
            :label="$t('Name')"
            required
            v-model="formData.name"
            :disabled="loading"
            :rules="[val => !!val || 'Please enter channel name.']"
          />
        </Col>
        <Col col="12">
          <Selector
            :label="$t('Type')"
            v-model="formData.type"
            :options="typeOptions"
            :disabled="loading || Boolean(formData.id)"
            required
            :rules="[val => !!val || $t('Please select type.')]"
          />
        </Col>
        <Col col="12" v-if="formData.type === 'msteams'">
          <Input
            name="msteamsWebhookUrl"
            :label="$t('Webhook URL')"
            required
            v-model="formData.msteamsWebhookUrl"
            :disable="loading"
            :rules="[val => !!val || $t('Please enter webhook URL.')]"
          />
        </Col>

        <Col col="12" v-if="formData.type === 'email'">
          <Row gutter="md">
            <Col col="12">
              <Selector
                :label="$t('Format')"
                v-model="formData.emailFormat"
                :options="formatOptions"
                :disabled="loading"
                required
              />
            </Col>

            <Col col="12" v-for="(_, key) in formData.emails" :key="key">
              <div class="emails">
                <div class="input">
                  <Input
                    v-if="formData.type === 'email'"
                    :name="`emails[${key}]`"
                    :label="key === 0 ? $t('Email') : ''"
                    required
                    v-model="formData.emails[key]"
                    :disabled="loading"
                    :rules="[val => !!val || $t('Please enter email.')]"
                  />
                </div>
                <div>
                  <Button
                    v-if="key > 0"
                    color="negative"
                    icon="delete"
                    flat
                    @click="deleteEmail(key)"
                    class="q-ml-sm"
                  />
                </div>
              </div>
            </Col>
            <Col>
              <Button :label="$t('Add email')" icon="add" size="small" @click="addEmail" />
            </Col>
          </Row>
        </Col>
      </Row>
      <div class="text-right q-gutter-sm q-mt-sm">
        <Button :label="$t('Close')" color="standard" flat @click="currentVisible = false" />
        <Button :label="$t('Submit')" type="submit" :loading="loading" />
      </div>
    </Form>
  </Dialog>
</template>

<script>
import { mapActions } from 'vuex';

import Button from '@design-system/components/Button.vue';
import Dialog from '@design-system/components/Dialog.vue';
import Form from '@design-system/components/Form.vue';
import Selector from '@design-system/components/Selector.vue';
import Input from '@design-system/components/Input.vue';
import Toggle from '@design-system/components/Toggle.vue';
import Row from '@design-system/components/Row.vue';
import Col from '@design-system/components/Col.vue';

import config from '@/config';

const { slackClientId } = config;

const messages = {
  en: {
    Channel: 'Channel',
    Enabled: 'Enabled',
    Name: 'Name',
    'Please enter channel name.': 'Please enter channel name.',
    Format: 'Format',
    'Add email': 'Add email',
    Email: 'Email',
    'Please enter email.': 'Please enter email.',
    'Webhook URL': 'Webhook URL',
    'Please enter webhook URL.': 'Please enter webhook URL.',
    Submit: 'Submit',
    channel_created: 'Channel Created',
    channel_edited: 'Channel Edited',
    error_occurred: 'Error Occurred',
  },
  ja: {
    Channel: 'チャネル',
    Enabled: '有効',
    Name: '名前',
    'Please enter channel name.': 'チャネル名を入力してください。',
    Format: 'フォーマット',
    'Add email': 'Eメールを追加',
    Email: 'Eメール',
    'Please enter email.': 'Eメールアドレスを入力してください。',
    'Webhook URL': 'Webhook URL',
    'Please enter webhook URL.': 'Webhook URL を入力してください。',
    Submit: '保存する',
    channel_created: 'チャンネル作成',
    channel_edited: 'チャンネル編集',
    error_occurred: 'エラー発生',
  },
};

const defaultFormData = {
  id: '',
  enabled: true,
  name: '',
  emailFormat: '',
  emails: [''],
  msteamsWebhookUrl: '',
};

export default {
  name: 'ModalChannelForm',
  i18n: { messages },
  components: { Dialog, Form, Input, Selector, Button, Toggle, Row, Col },
  props: {
    value: Boolean,
    typeOptions: Array,
    formatOptions: Array,
    channelData: Object,
  },
  watch: {
    channelData(value) {
      this.formData = {
        ...this.formData,
        ...(Object.keys(value).length
          ? value
          : {
              ...defaultFormData,
              type: this.typeOptions[0].value,
              emailFormat: this.formatOptions[0].value,
            }),
      };
    },
    'formData.type': function(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal === 'email') {
          if (this.formData.emails.length === 0) {
            this.formData.emails = [''];
          }
        } else {
          this.formData.emails = [];
        }
      }
    },
  },
  data() {
    return {
      loading: false,
      formData: {
        ...defaultFormData,
        type: this.typeOptions[0].value,
        emailFormat: this.formatOptions[0].value,
      },
    };
  },
  computed: {
    currentVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
  },
  methods: {
    ...mapActions('notificationSettings', { saveNotificationChannels: 'saveNotificationChannels' }),
    async onSubmit() {
      if (this.formData.type === 'slack' && !this.formData.id) {
        const redirectUrl = window.location.href.includes('localhost')
          ? 'https://appdev.alphaus.cloud/wavepro/preferences'
          : window.location.href;
        const state = btoa(
          JSON.stringify({
            ...this.formData,
            slackRedirectUri: redirectUrl,
          })
        );
        window.location.href = `https://slack.com/oauth/v2/authorize?client_id=${slackClientId}&scope=incoming-webhook&state=${state}&redirect_uri=${redirectUrl}&user_scope=`;
        return;
      }
      this.loading = true;
      try {
        await this.saveNotificationChannels(this.formData);
        if (this.formData.id) {
          this.$alpha.notify.custom({ type: 'success', message: this.$t('channel_edited') });
        } else {
          this.$alpha.notify.custom({ type: 'success', message: this.$t('channel_created') });
        }
      } catch (err) {
        await this.$alpha.notify.custom({ type: 'danger', message: this.$t('error_occurred') });
      }
      this.loading = false;
      this.currentVisible = false;
    },

    onChangeEnabled(value) {
      this.formData.enabled = value;
    },
    addEmail() {
      this.formData.emails = [...this.formData.emails, ''];
    },
    deleteEmail(key) {
      this.formData.emails = this.formData.emails.filter((_, i) => key !== i);
    },
  },
};
</script>

<style lang="scss" scoped>
.extra-email-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.email-input {
  flex: 1;
}

.delete-button {
  margin-top: 6px;
}

.emails {
  display: flex;

  .input {
    flex: 1;
  }
}
</style>
